import { ref, computed, type Ref } from "vue";

export type Step = "review-requirements" | "provide-details";

export interface StepDefinition {
	key: Step;
	label: string;
	icon: string;
}

export type RunAgentDialogMode = "run" | "collect";

export interface UseRunAgentStepsOptions {
	hasPlatformRequirements: Ref<boolean>;
	hasUserInputItems: Ref<boolean>;
	allPlatformsAuthenticated: Ref<boolean>;
	allUserInputValid: Ref<boolean>;
	selectedPack: Ref<any>;
	runningAnalysis: Ref<boolean>;
	mode?: Ref<RunAgentDialogMode>;
}

export function useRunAgentSteps(options: UseRunAgentStepsOptions) {
	const {
		hasPlatformRequirements,
		hasUserInputItems,
		allPlatformsAuthenticated,
		allUserInputValid,
		runningAnalysis,
		mode,
	} = options;

	const isCollectMode = computed(() => mode?.value === "collect");

	const currentStep = ref<Step>("review-requirements");

	const stepperSteps = computed<StepDefinition[]>(() => {
		const steps: StepDefinition[] = [];
		if (hasPlatformRequirements.value) {
			steps.push({ key: "review-requirements", label: "Review Requirements", icon: "mdi-connection" });
		}
		if (hasUserInputItems.value) {
			steps.push({ key: "provide-details", label: "Provide Details", icon: "mdi-form-textbox" });
		}
		return steps;
	});

	const currentStepIndex = computed(() => {
		return stepperSteps.value.findIndex((s) => s.key === currentStep.value);
	});

	const isLastStep = (step: Step) => {
		const steps = stepperSteps.value;
		return steps.length > 0 && steps[steps.length - 1].key === step;
	};

	const finalActionText = computed(() => (isCollectMode.value ? "Done" : "Run Report"));

	const buttonText = computed(() => {
		if (runningAnalysis.value) {
			return "Running...";
		}
		if (currentStep.value === "review-requirements") {
			if (!allPlatformsAuthenticated.value) {
				return "Log in to continue";
			}
			if (isLastStep("review-requirements")) {
				return finalActionText.value;
			}
			return "Continue";
		}
		if (currentStep.value === "provide-details") {
			// Always the ACTION, never the error (LSP-19461). This used to read "Complete required fields"
			// while the form was invalid, so the button described the problem and nothing on screen pointed
			// at the field. The field carries its own error now; the button says what clicking it does.
			return finalActionText.value;
		}
		return "Continue";
	});

	const canProceedFromCurrentStep = computed(() => {
		if (currentStep.value === "review-requirements") {
			return allPlatformsAuthenticated.value;
		}
		if (currentStep.value === "provide-details") {
			return allUserInputValid.value;
		}
		return true;
	});

	return {
		currentStep,
		stepperSteps,
		currentStepIndex,
		buttonText,
		canProceedFromCurrentStep,
	};
}
